import _extends from "@babel/runtime/helpers/extends";
import { Box, Button, createStyles, IconButton, makeStyles, TextField } from '@material-ui/core';
import { AccountTree } from '@material-ui/icons';
import { ConstantContent } from '@sensenet/client-core';
import { debounce } from '@sensenet/client-utils';
import { Query, QueryExpression, QueryOperators } from '@sensenet/query';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SelectionContext, SelectionProvider } from "../../context/selection.js";
import { SaveButton } from "../save-button.js";
import { SearchPicker } from "../search-picker.js";
import { SelectionList } from "../selection-list.js";
import { ShowSelectedButton } from "../show-selected-button.js";
import { TreePicker } from "../tree-picker/index.js";
import { CopyMoveTreePicker } from "../tree-picker/copy-move-tree-picker.js";
import { PickerHelper } from "./picker-helper.js";
export let PickerModes;
(function (PickerModes) {
  PickerModes[PickerModes["TREE"] = 0] = "TREE";
  PickerModes[PickerModes["COPY_MOVE_TREE"] = 1] = "COPY_MOVE_TREE";
  PickerModes[PickerModes["SEARCH"] = 2] = "SEARCH";
  PickerModes[PickerModes["SELECTION"] = 3] = "SELECTION";
})(PickerModes || (PickerModes = {}));
const useStyles = makeStyles(theme => {
  return createStyles({
    treeIcon: {
      color: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.5)' : undefined
    },
    treeActiveIcon: {
      color: theme.palette.type === 'light' ? theme.palette.common.black : theme.palette.common.white
    },
    showSelected: {
      textAlign: 'right',
      marginLeft: 'auto',
      marginTop: '5px'
    },
    jumpCurrentPath: {
      cursor: 'pointer'
    },
    toolbar: {
      display: 'flex',
      alignItems: 'center',
      paddingRight: '24px',
      flexWrap: 'wrap',
      marginBottom: '10px',
      '& .MuiFormControl-root': {
        width: 'calc(100% - 48px)'
      }
    },
    cancelButton: {},
    currentLocationIcon: {
      width: 20,
      height: 20,
      borderRadius: '50%',
      backgroundColor: 'blue'
    }
  });
});
export const Picker = props => {
  var _props$treePickerMode, _props$localization$t, _props$localization, _props$localization$s, _props$localization2, _props$localization$s2, _props$localization3, _props$localization4;
  const treePickerMode = (_props$treePickerMode = props.treePickerMode) !== null && _props$treePickerMode !== void 0 ? _props$treePickerMode : PickerModes.TREE;
  // const contextPath = props.contextPath!

  const [navigationPath, setNavigationPath] = useState(props.currentPath);
  const [term, setTerm] = useState();
  const [result, setResult] = useState([]);
  const [searchError, setSearchError] = useState();
  const [mode, setMode] = useState(treePickerMode);
  const classes = useStyles(props);
  const searchFieldRef = useRef(null);
  const PickerContainer = props.pickerContainer || 'div';
  const ActionsContainer = props.actionsContainer || 'div';

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedQuery = useCallback(debounce(a => setTerm(a), 250), []);
  useEffect(() => {
    const ac = new AbortController();
    const fetchResult = async () => {
      if (!term) {
        setResult([]);
        return;
      }
      try {
        const getQueryFromTerm = () => {
          const query = new Query(q => q.query(q2 => q2.equals('Name', `*${term}*`).or.equals('DisplayName', `*${term}*`).autofilters('OFF')));
          if (props.selectionRoots) {
            new QueryOperators(query).and.query(q2 => {
              var _props$selectionRoots;
              (_props$selectionRoots = props.selectionRoots) === null || _props$selectionRoots === void 0 ? void 0 : _props$selectionRoots.forEach((root, index, array) => {
                new QueryExpression(q2.queryRef).inTree(root);
                if (index < array.length - 1) {
                  return new QueryOperators(q2.queryRef).or;
                }
              });
              return q2;
            });
          }
          return query;
        };
        setSearchError('');
        const response = await props.repository.loadCollection({
          path: ConstantContent.PORTAL_ROOT.Path,
          oDataOptions: {
            query: getQueryFromTerm().toString()
          },
          requestInit: {
            signal: ac.signal
          }
        });
        setResult(response.d.results);
      } catch (e) {
        if (!ac.signal.aborted) {
          setSearchError(e.message);
          setResult([]);
        }
      }
    };
    fetchResult();
    return () => ac.abort();
  }, [term, props.repository, props.selectionRoots]);
  const handleJumpToCurrentPath = path => {
    setNavigationPath(path);
    debouncedQuery('');
    setMode(PickerModes.TREE);
  };
  return /*#__PURE__*/React.createElement(SelectionProvider, {
    allowMultiple: props.allowMultiple,
    selectionChangeCallback: props.onSelectionChanged,
    defaultValue: props.defaultValue
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      flexDirection: 'column',
      flexWrap: 'nowrap',
      height: '85%',
      justifyContent: 'space-between'
    }
  }, /*#__PURE__*/React.createElement(Box, {
    className: classes.toolbar
  }, /*#__PURE__*/React.createElement(IconButton, {
    title: (_props$localization$t = (_props$localization = props.localization) === null || _props$localization === void 0 ? void 0 : _props$localization.treeViewButton) !== null && _props$localization$t !== void 0 ? _props$localization$t : 'Tree view',
    onClick: () => setMode(treePickerMode),
    className: `${classes.treeIcon} ${mode === treePickerMode ? classes.treeActiveIcon : ''}`
  }, /*#__PURE__*/React.createElement(AccountTree, null)), /*#__PURE__*/React.createElement(TextField, {
    ref: searchFieldRef,
    fullWidth: true,
    placeholder: (_props$localization$s = (_props$localization2 = props.localization) === null || _props$localization2 === void 0 ? void 0 : _props$localization2.searchPlaceholder) !== null && _props$localization$s !== void 0 ? _props$localization$s : 'Search',
    onFocus: () => setMode(PickerModes.SEARCH),
    onChange: ev => {
      debouncedQuery(ev.target.value);
    }
  }), /*#__PURE__*/React.createElement(ShowSelectedButton, {
    className: classes.showSelected,
    handleClick: () => setMode(PickerModes.SELECTION),
    localization: {
      label: (_props$localization$s2 = (_props$localization3 = props.localization) === null || _props$localization3 === void 0 ? void 0 : _props$localization3.showSelected) !== null && _props$localization$s2 !== void 0 ? _props$localization$s2 : 'Show selected'
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: "selection-container",
    style: {
      width: '100%',
      display: 'flex'
    }
  }, /*#__PURE__*/React.createElement(PickerHelper, {
    handleJumpToCurrentPath: handleJumpToCurrentPath,
    contextPath: props.contextPath,
    styles: classes.jumpCurrentPath,
    repository: props.repository,
    selectionRoots: props.selectionRoots,
    currentContentText: (_props$localization4 = props.localization) === null || _props$localization4 === void 0 ? void 0 : _props$localization4.currentContentText
  }), /*#__PURE__*/React.createElement(PickerContainer, {
    style: {
      height: '545px',
      paddingTop: 0,
      position: 'relative',
      top: '-7px',
      width: '100%'
    }
  }, mode === PickerModes.TREE && /*#__PURE__*/React.createElement(TreePicker, _extends({
    setNavigationPath: setNavigationPath,
    navigationPath: navigationPath
  }, props)), mode === PickerModes.COPY_MOVE_TREE && /*#__PURE__*/React.createElement(CopyMoveTreePicker, props), mode === PickerModes.SEARCH && /*#__PURE__*/React.createElement(SearchPicker, _extends({}, props, {
    items: result,
    error: searchError
  })), mode === PickerModes.SELECTION && /*#__PURE__*/React.createElement(SelectionList, props)))), /*#__PURE__*/React.createElement(SelectionContext.Consumer, null, ({
    selection
  }) => {
    var _props$localization$c, _props$localization5, _props$localization$c2, _props$localization6, _props$localization$s3, _props$localization7;
    return props.renderActions ? props.renderActions(selection) : /*#__PURE__*/React.createElement(ActionsContainer, {
      style: {
        marginLeft: 'auto',
        width: '100%'
      }
    }, /*#__PURE__*/React.createElement(Button, {
      "aria-label": (_props$localization$c = (_props$localization5 = props.localization) === null || _props$localization5 === void 0 ? void 0 : _props$localization5.cancelButton) !== null && _props$localization$c !== void 0 ? _props$localization$c : 'Cancel',
      className: classes.cancelButton,
      disabled: !!props.isExecInProgress,
      onClick: () => {
        var _props$handleCancel;
        return (_props$handleCancel = props.handleCancel) === null || _props$handleCancel === void 0 ? void 0 : _props$handleCancel.call(props);
      }
    }, (_props$localization$c2 = (_props$localization6 = props.localization) === null || _props$localization6 === void 0 ? void 0 : _props$localization6.cancelButton) !== null && _props$localization$c2 !== void 0 ? _props$localization$c2 : 'Cancel'), /*#__PURE__*/React.createElement(SaveButton, {
      "data-test": "picker-submit",
      disabled: props.required && selection.length < props.required || !!props.isExecInProgress,
      localization: {
        label: (_props$localization$s3 = (_props$localization7 = props.localization) === null || _props$localization7 === void 0 ? void 0 : _props$localization7.submitButton) !== null && _props$localization$s3 !== void 0 ? _props$localization$s3 : 'Submit'
      },
      onClick: () => {
        var _props$handleSubmit;
        return (_props$handleSubmit = props.handleSubmit) === null || _props$handleSubmit === void 0 ? void 0 : _props$handleSubmit.call(props, selection);
      }
    }));
  }));
};
//# sourceMappingURL=picker.js.map